import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { requestJSON } from "../api";
import { useI18n } from "../i18n";
import {
  ManagementDialog,
  ManagementPanel,
  ManagementPagination,
} from "../managementUI";
import { date, duration, stateLabel, type ExecutionRecord } from "./model";
import { Empty, Note, Summary, Table } from "./ui";

type PageData = {
  data_center_id: string;
  observed_at: string;
  coverage: string;
  summary_basis: string;
  state_counts: Record<string, number>;
  current_counts: Record<string, number>;
  total: number;
  limit: number;
  offset: number;
  items: ExecutionRecord[];
};
export function PersistentSandboxExecution({
  refreshKey,
}: {
  refreshKey: number;
}) {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [hours, setHours] = useState(24);
  const [team, setTeam] = useState("");
  const [draftTeam, setDraftTeam] = useState("");
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState<PageData | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ExecutionRecord | null>(null);
  const window = useMemo(() => {
    const end = new Date();
    return {
      start: new Date(end.getTime() - hours * 3600_000).toISOString(),
      end: end.toISOString(),
    };
  }, [hours, revision, refreshKey]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    setData(null);
    const query = new URLSearchParams({
      ...window,
      team_id: team,
      status,
      limit: "20",
      offset: String(offset),
    });
    requestJSON<PageData>(`/api/v1/monitoring/sandbox-executions?${query}`, {
      signal: controller.signal,
    })
      .then((value) => {
        if (!controller.signal.aborted) setData(value);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setData(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [window, team, status, offset]);
  useEffect(() => {
    const timer = globalThis.setInterval(() => {
      if (!offset && document.visibilityState === "visible")
        setRevision((v) => v + 1);
    }, 30000);
    return () => globalThis.clearInterval(timer);
  }, [offset]);
  const total = data
    ? Object.values(data.state_counts).reduce((a, b) => a + b, 0)
    : null;
  return (
    <ManagementPanel
      title={t("业务执行记录", "Execution records")}
      actions={
        <button
          className="secondary-button"
          disabled={loading}
          onClick={() => {
            setOffset(0);
            setRevision((v) => v + 1);
          }}
        >
          {t("刷新统计", "Refresh statistics")}
        </button>
      }
    >
      <div className="monitor-toolbar">
        <label>
          {t("执行类型", "Source")}
          <select
            value="sandbox"
            onChange={(e) => {
              const next = new URLSearchParams(params);
              next.set("execution_source", e.target.value);
              setParams(next);
            }}
          >
            <option value="sandbox">
              {t(
                "Sandbox 持久执行统计",
                "Sandbox retained execution statistics",
              )}
            </option>
            <option value="registrations">
              {t("Team 创建 / 建库", "Team provisioning")}
            </option>
            <option value="publish">{t("应用发布", "Publishing")}</option>
            <option value="operations">
              {t("管理操作 / 迁移", "Operations")}
            </option>
            <option value="task">
              {t("Task / AgentTask 业务统计", "Task / AgentTask statistics")}
            </option>
            <option value="conversation">
              {t("对话回复 / 模型调用", "Replies / model calls")}
            </option>
          </select>
        </label>
        <label>
          {t("创建时间范围", "Created within")}
          <select
            value={hours}
            onChange={(e) => {
              setHours(Number(e.target.value));
              setOffset(0);
            }}
          >
            {[1, 24, 168, 720].map((h) => (
              <option key={h} value={h}>
                {h === 1
                  ? t("1小时", "1 hour")
                  : h === 24
                    ? t("24小时", "24 hours")
                    : h === 168
                      ? t("7天", "7 days")
                      : t("30天", "30 days")}
              </option>
            ))}
          </select>
        </label>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setTeam(draftTeam.trim());
            setOffset(0);
          }}
          className="monitor-team-filter"
        >
          <label>
            Team ID
            <input
              value={draftTeam}
              maxLength={128}
              onChange={(e) => setDraftTeam(e.target.value)}
            />
          </label>
          <button className="secondary-button" type="submit">
            {t("筛选", "Filter")}
          </button>
        </form>
        <label>
          {t("状态", "State")}
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">{t("全部", "All")}</option>
            {[
              "pending",
              "queued",
              "running",
              "completed",
              "failed",
              "canceled",
              "expired",
            ].map((s) => (
              <option key={s} value={s}>
                {stateLabel(s, t)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <Note>
        {t(
          "数量直接统计当前数据中心保留的执行记录，与现存环境列表无关。区间统计按创建时间归组；已删除的持久记录不在覆盖范围内。Sandbox 执行不等同于 Pacture Task。",
          "Counts come from retained execution records in this DC, independently of live environments. Period counts use creation time. Deleted records are outside coverage. A Sandbox execution is not a Pacture Task.",
        )}
      </Note>
      <Summary
        items={[
          {
            label: t("当前非终态执行", "Current nonterminal executions"),
            value: data
              ? String(
                  Object.values(data.current_counts).reduce((a, b) => a + b, 0),
                )
              : "—",
            note: t("不受创建时间范围限制", "Independent of creation window"),
          },
          {
            label: t("区间创建总量", "Created in interval"),
            value: total === null ? "—" : String(total),
          },
          {
            label: t("其中已完成", "Completed among these"),
            value: data ? String(data.state_counts.completed || 0) : "—",
          },
          {
            label: t("其中失败 / 过期", "Failed / expired among these"),
            value: data
              ? `${data.state_counts.failed || 0} / ${data.state_counts.expired || 0}`
              : "—",
          },
        ]}
      />
      <Note>
        {t(
          "状态筛选只作用于下方记录列表；上方数量保留整个创建区间的状态分布。",
          "The state filter applies only to the record list; summaries retain the full creation-cohort distribution.",
        )}{" "}
        {data ? t("统计时点：", "Observed: ") + date(data.observed_at) : ""}
      </Note>
      {error ? (
        <Empty>
          {t(
            "执行记录数据源不可用或已过期。",
            "Execution source unavailable or stale.",
          )}
        </Empty>
      ) : loading ? (
        <Empty>{t("正在读取统计…", "Loading statistics…")}</Empty>
      ) : data ? (
        <>
          {!data.items.length ? (
            <Empty>
              {t("所选范围没有记录。", "No records in this range.")}
            </Empty>
          ) : (
            <Table
              headers={[
                t("执行", "Execution"),
                "Team",
                t("状态", "State"),
                t("创建", "Created"),
                t("到终态耗时", "Time to terminal state"),
                t("操作", "Actions"),
              ]}
            >
              {data.items.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.id}</strong>
                    <span>{row.kind}</span>
                  </td>
                  <td>{row.team_id}</td>
                  <td>{stateLabel(row.state, t)}</td>
                  <td>{date(row.created_at)}</td>
                  <td>
                    {row.completed_at
                      ? duration(
                          (Date.parse(row.completed_at) -
                            Date.parse(row.created_at)) /
                            1000,
                        )
                      : "—"}
                  </td>
                  <td>
                    <button
                      className="secondary-button"
                      onClick={() => setSelected(row)}
                    >
                      {t("详情", "Details")}
                    </button>
                  </td>
                </tr>
              ))}
            </Table>
          )}
          <ManagementPagination
            total={data.total}
            offset={offset}
            limit={20}
            onChange={setOffset}
          />
        </>
      ) : null}
      {selected ? (
        <ManagementDialog
          title={t("Sandbox 执行详情", "Sandbox execution details")}
          onClose={() => setSelected(null)}
        >
          <dl className="monitor-detail">
            <dt>ID</dt>
            <dd>{selected.id}</dd>
            <dt>Team</dt>
            <dd>{selected.team_id}</dd>
            <dt>Workspace</dt>
            <dd>{selected.resource_id}</dd>
            <dt>{t("状态", "State")}</dt>
            <dd>{stateLabel(selected.state, t)}</dd>
            <dt>{t("进入终态时刻", "Terminal transition time")}</dt>
            <dd>{date(selected.completed_at)}</dd>
          </dl>
          <button
            className="secondary-button"
            onClick={() => setSelected(null)}
          >
            {t("关闭", "Close")}
          </button>
        </ManagementDialog>
      ) : null}
    </ManagementPanel>
  );
}
